import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { PermissionService } from '../../services/permission.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { UnitTestsService } from '../../services/unit-tests.service';
import { QuoteItem } from '../../../overview/overview.data.service';
import { openQuoteDeleteDialog } from '../quote-delete-dialog/quote-delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { QuoteConversion, QuoteService } from '../../services/quote.service';
import { openTemplateUnitTestEditDialog } from '../template-unit-test-edit-dialog/template-unit-test-edit-dialog.component';
import { openQuoteCreationDialog } from '../quote-creation-dialog/quote-creation-dialog.component';
import { AppService } from '../../services/app.service';

export interface UnitTestDeltaProducts {
    id: string;
    material: string;
    quantity?: number;
    oldQuantity?: number;
    productDescription?: string;
    price?: string;
}

export interface UnitTestDelta {
    line: string;
    added: UnitTestDeltaProducts[];
    removed: UnitTestDeltaProducts[];
    modified: UnitTestDeltaProducts[];
    configuration: UnitTestDeltaConfiguration;
    genericProducts: UnitTestDeltaGenericProducts;
    schemes: UnitTestDeltaSchemes;
}

export interface UnitTestDeltaGenericProducts {
    added: UnitTestDeltaProducts[];
    modified: UnitTestDeltaProducts[];
}

export interface UnitTestDeltaSchemes {
    primary: UnitTestDeltaScheme;
    secondary: UnitTestDeltaScheme;
}

export interface UnitTestDeltaScheme {
    added: string[];
    removed: string[];
}

export interface UnitTestDeltaConfiguration {
    complete: boolean;
    invalidMessage: string;
    valid: boolean;
}

export interface UnitRestResponse {
    linesDelta: UnitTestDelta[];
    valid: boolean;
    priceCalculationError: string;
}

export interface PimProduct {
    _id: string;
    name: string;
    description?: string;
}

export interface UnitTest extends QuoteItem {
    loading: boolean;
    unitTestResponse: UnitRestResponse;
}

@Component({
    selector: 'app-unit-tests',
    templateUrl: './unit-tests.component.html',
    styleUrls: ['./unit-tests.component.scss'],
})
export class UnitTestsComponent implements OnInit {
    public unitTests: UnitTest[] = [];

    @Input() public unitTestsData;
    public disableTrigger = false;

    @Input()
    public isAdmin: boolean;

    @Output() public reloadUnitTests = new EventEmitter();
    @Output() public useUnitTest = new EventEmitter<string>();

    constructor(
        private unitTestsService: UnitTestsService,
        private permissionService: PermissionService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private quoteService: QuoteService,
        public appService: AppService
    ) {}

    ngOnInit(): void {
        this.unitTestsData.subscribe((data) => (this.unitTests = data));
    }

    public startAllUnitTests() {
        this.unitTests.forEach((unitTest) => {
            this.startSingleUnitTest(unitTest.id);
        });
    }

    public startSingleUnitTest(id: string) {
        this.disableTrigger = true;
        const unitTest = this.unitTests.find((d) => d.id === id);
        unitTest.loading = true;
        this.unitTestsService
            .startSingleUnitTest(id)
            .pipe(
                switchMap((response: UnitRestResponse) => {
                    unitTest.unitTestResponse = response;

                    if (!response.valid && this.unitTestHasProducts(response)) {
                        return this.unitTestsService.getPimProducts(this.extractUnitTestMaterials(response)).pipe(
                            map((PimProducts: PimProduct[]) => {
                                this.mapPimProductToUnitTestResponse(response, PimProducts);
                                return null;
                            }),
                            catchError(() => {
                                return of(null);
                            })
                        );
                    } else {
                        return of(null);
                    }
                }),
                catchError(() => {
                    return of(null);
                })
            )
            .subscribe(
                () => {
                    unitTest.loading = false;
                    this.disableTrigger = false;
                },
                () => this.onError('OVERVIEW.UNIT_TESTS.SNACK_BAR.EXECZTE_UNIT_TEST.FAILURE')
            );
    }

    private extractUnitTestMaterials(unitTest: UnitRestResponse) {
        return unitTest.linesDelta.flatMap((delta) => {
            const { added, removed, modified, genericProducts } = delta;
            const lineMaterials = [...added, ...removed, ...modified].map((d) => d.material);
            const genericMaterials = [
                ...(genericProducts?.added || []).map((d) => d.material),
                ...(genericProducts?.modified || []).map((d) => d.material),
            ];
            return [...lineMaterials, ...genericMaterials];
        });
    }

    private mapPimProductToUnitTestResponse(unitTest: UnitRestResponse, pimProducts: PimProduct[]) {
        const expand = (d: UnitTestDeltaProducts) => {
            d.productDescription = pimProducts.find((m) => m.name === d.material)?.description;
            return d;
        };
        return {
            delta: unitTest.linesDelta.map((delta) => ({
                added: delta.added.map(expand),
                removed: delta.removed.map(expand),
                modified: delta.modified.map(expand),
            })),
            genericProducts: {
                added: unitTest.linesDelta.flatMap((delta) => (delta.genericProducts?.added || []).map(expand)),
                removed: unitTest.linesDelta.flatMap((delta) => (delta.genericProducts?.modified || []).map(expand)),
            },
        };
    }

    private onError(translationKey: string) {
        if (this.permissionService.isUserAuthorized$.value) {
            setTimeout(() => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant(translationKey),
                    isFailure: true,
                });
            }, 200);
        }
    }

    private unitTestHasProducts(response: UnitRestResponse): boolean {
        return (
            response.linesDelta?.some(
                (delta) =>
                    (delta.added?.length ?? 0) > 0 ||
                    (delta.removed?.length ?? 0) > 0 ||
                    (delta.modified?.length ?? 0) > 0 ||
                    (delta.genericProducts?.added.length ?? 0) > 0 ||
                    (delta.genericProducts?.modified.length ?? 0) > 0
            ) ?? false
        );
    }

    public onDeleteClick(id: string, name: string) {
        openQuoteDeleteDialog(
            this.dialog,
            this.translateService.instant('OVERVIEW.DIALOG.DELETE_UNIT_TEST.TITLE'),
            `${this.translateService.instant('OVERVIEW.DIALOG.DELETE_UNIT_TEST.MESSAGE')} ${name}`,
            this.translateService.instant('COMMON.DIALOG.CONFIRM'),
            this.translateService.instant('COMMON.DIALOG.CANCEL')
        )
            .pipe(
                filter((confirmed) => !!confirmed),
                switchMap(() => this.quoteService.convertQuote({ id, type: 'quote' }))
            )
            .subscribe({
                next: () => {
                    this.snackBarService.openSnackBar({
                        message: this.translateService.instant('COMMON.OVERVIEW.DELETE.SUCCESS'),
                        isFailure: false,
                    });
                    this.reloadUnitTests.emit();
                },
            });
    }

    public onEditClick(unitTest: UnitTest) {
        openTemplateUnitTestEditDialog(this.dialog, unitTest)
            .pipe(
                filter((conversion: QuoteConversion | null | undefined) => !!conversion),
                switchMap((conversion: QuoteConversion) => this.quoteService.convertQuote(conversion))
            )
            .subscribe({
                next: () => {
                    this.snackBarService.openSnackBar({
                        message: this.translateService.instant('COMMON.OVERVIEW.EDIT.SUCCESS'),
                        isFailure: false,
                    });
                    this.reloadUnitTests.emit();
                },
            });
    }

    public onUseClick(unitTest: UnitTest) {
        const id = unitTest.id;
        openQuoteCreationDialog(
            this.dialog,
            this.translateService.instant('OVERVIEW.DIALOG.USE_UNIT_TEST.TITLE'),
            this.translateService.instant('OVERVIEW.DIALOG.USE_UNIT_TEST.CONFIRM'),
            this.translateService.instant('COMMON.DIALOG.CANCEL'),
            `${this.translateService.instant('OVERVIEW.DIALOG.COPY_OFFER.COPY')} ${unitTest.customerPurchaseNumber}`
        )
            .pipe(
                filter((customerPurchaseNumber) => !!customerPurchaseNumber),
                switchMap((customerPurchaseNumber) =>
                    this.quoteService.convertQuote({ id, type: 'quote', customerPurchaseNumber })
                )
            )
            .subscribe({
                next: () => {
                    this.snackBarService.openSnackBar({
                        message: this.translateService.instant('COMMON.OVERVIEW.USE.SUCCESS'),
                        isFailure: false,
                    });
                    this.useUnitTest.emit(id);
                },
            });
    }
}
